import { cn } from "@/lib/cn"

type Tone = "brand" | "ink" | "emerald" | "amber" | "red" | "violet"

const fills: Record<Tone, string> = {
  brand: "bg-brand-500",
  ink: "bg-ink-700",
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  red: "bg-red-500",
  violet: "bg-violet-500",
}

const sizes = {
  sm: "h-1",
  md: "h-2",
  lg: "h-3",
} as const

type Props = {
  value: number
  max?: number
  tone?: Tone
  size?: keyof typeof sizes
  className?: string
  trackClassName?: string
  ariaLabel?: string
  // While true, the fill carries a moving diagonal sheen so an in-progress run
  // reads as actively working, and the width eases between updates.
  animated?: boolean
}

export function ProgressBar({ value, max = 100, tone = "brand", size = "md", className, trackClassName, ariaLabel, animated = false }: Props) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      className={cn("w-full overflow-hidden rounded-full", trackClassName ?? "bg-ink-100", sizes[size], className)}
    >
      <span
        aria-hidden="true"
        className={cn("relative block h-full overflow-hidden rounded-full transition-[width] duration-700 ease-out", fills[tone])}
        style={{ width: `${pct}%` }}
      >
        {animated && pct > 0 && <span aria-hidden="true" className="progress-stripes absolute inset-0" />}
      </span>
    </div>
  )
}
